import type { Extension } from "@medplum/fhirtypes";
import { assertFactCondition, type QuestionnaireFactCondition } from "@repo/domain";
import { PATIENT_FACT_CONDITION_URL } from "@repo/fhir-systems/extensions";

/** Position preserves an interleaved list split between FHIR questions and fact extensions. */
export function factConditionExtensions(conditions: readonly object[] | undefined): Extension[] {
  return (conditions ?? []).flatMap((condition, position) => {
    if (!("fact" in condition)) return [];
    const fact = condition as QuestionnaireFactCondition;
    return [
      {
        url: PATIENT_FACT_CONDITION_URL,
        extension: [
          { url: "fact", valueCode: fact.fact },
          { url: "operator", valueCode: fact.operator },
          { url: "value", valueInteger: fact.value },
          { url: "position", valueInteger: position },
        ],
      },
    ];
  });
}

export function withFactConditions<T>(
  conditions: readonly T[],
  extensions: readonly Extension[] | undefined,
): (T | QuestionnaireFactCondition)[] {
  const result: (T | QuestionnaireFactCondition)[] = [...conditions];
  const facts = (extensions ?? [])
    .filter((e) => e.url === PATIENT_FACT_CONDITION_URL)
    .map((e) => {
      const get = (name: string) => e.extension?.find((part) => part.url === name);
      const condition = {
        fact: get("fact")?.valueCode,
        operator: get("operator")?.valueCode,
        value: get("value")?.valueInteger,
      } as QuestionnaireFactCondition;
      assertFactCondition(condition);
      return { condition, position: get("position")?.valueInteger ?? result.length };
    })
    .sort((a, b) => a.position - b.position);
  for (const { condition, position } of facts) result.splice(position, 0, condition);
  return result;
}
